import { Dispatch, ElementType, ReactNode, SetStateAction, useEffect } from 'react';
import { cx } from '@linaria/atomic';
import { createGetSpecificChildren } from '../../helpers';
import { PolymorphicComponentPropWithoutRef } from '../../type-utils';
import { SwitchChip, SwitchChipProps } from './switch-chip';
import { SwitchChipGroupContext } from './switch-chip-group-context';
import { switchChipGroupStyle } from './switch-chip.styles';

export type SwitchChipGroupOptional = {
  children: ReactNode;
  value: string[];
  setValue: Dispatch<SetStateAction<string[]>>;
  defaultValue?: string;
  disabled?: boolean;
  singleSelect?: boolean;
  checkboxMode?: boolean;
  onSelect?: (value: string[]) => void;
};

type PolymorphicProps<T extends ElementType> = PolymorphicComponentPropWithoutRef<T, SwitchChipGroupOptional>;

const SwitchChipGroupOptional = <T extends ElementType = 'div'>({
  as,
  children,
  value,
  setValue,
  defaultValue,
  disabled,
  singleSelect,

  /**
   * Checkbox Mode:
   * 1) Do not select first value, if value/defaultValue are empty.
   * 2) Allow unchecking of all options
   */
  checkboxMode = false,
  onSelect,
  className,
  ...rest
}: PolymorphicProps<T>) => {
  const Component = as || 'div';
  const getChildren = createGetSpecificChildren<'SwitchChip', SwitchChipProps>(children, true);
  const chipsChildren = getChildren('SwitchChip');
  const childrenValues = chipsChildren.map(({ props }) => props.value);

  const toggleValue = (valueToToggle: string) => {
    if (value.length === 1 && value.includes(valueToToggle) && !checkboxMode) {
      return;
    }

    if (singleSelect) {
      setValue([valueToToggle]);
      onSelect?.([valueToToggle]);
      return;
    }

    if (value.includes(valueToToggle)) {
      setValue((prev) => {
        const newValueToToggle = prev.filter((val) => val !== valueToToggle);
        onSelect?.(newValueToToggle);
        return newValueToToggle;
      });
    } else {
      setValue((prev) => {
        const newValueToToggle = [...prev, valueToToggle];
        onSelect?.(newValueToToggle);
        return newValueToToggle;
      });
    }
  };

  useEffect(() => {
    if (!!value.length) return;

    if (defaultValue && childrenValues.includes(defaultValue)) {
      setValue([defaultValue]);
      return;
    }

    const firstEnabledChip = chipsChildren.filter(({ props }) => !props.disabled)[0];
    if (firstEnabledChip && !checkboxMode) {
      setValue([firstEnabledChip.props.value]);
    }
  }, [value.length]);

  useEffect(() => {
    if (value.length > 1 && singleSelect) {
      setValue((prev) => prev.slice(0, 1));
    }
  }, [singleSelect]);

  return (
    <SwitchChipGroupContext.Provider
      value={{
        value,
        toggleValue,
        disabled,
      }}
    >
      <Component className={cx(switchChipGroupStyle, className)} {...rest} role='group'>
        {chipsChildren}
      </Component>
    </SwitchChipGroupContext.Provider>
  );
};

/**
 * SwitchChipGroup component is used to group multiple SwitchChip components together.
 * @param children - SwitchChip components to be rendered within the group.
 * @param value - current selected values of the chips in the group.
 * @param setValue - function to update the selected values of the chips in the group.
 * @param onSelect - optional callback function to do additional actions when the chip is selected. It receives the new array of selected values as its argument.
 * @param defaultValue - initial value to be selected when the group is rendered. If not provided, the first enabled chip will be selected.
 * @param disabled - whether the entire group is disabled, defaults to false.
 * @param singleSelect - whether the group allows only one chip to be selected at a time, defaults to false.
 * @param checkboxMode - whether the group allows unchecking of all options and does not select the first value if `value` or `defaultValue` are empty, defaults to false.
 *
 * SwitchChipGroup.Option uses the SwitchChip component.
 * @param value - value of the chip, which is used to identify the chip in the group.
 * @param disabled - whether the chip is disabled, defaults to false.
 * @param isNew - whether the chip is a new design. This prop is used to apply the new design styles. All new implementations should be `isNew: true`. Designers have requested that the new design is used for all new chips. TODO: Remove the `isNew` prop once all consumers have been updated.
 */

export const SwitchChipGroup = Object.assign(SwitchChipGroupOptional, {
  Option: SwitchChip,
});
